<template>
  <div class="game-scatter-mobile">
    <div class="chart-card">
      <div class="card-header">
        <span class="card-title">赛事散点图</span>
        <div class="card-actions">
          <el-button-group class="mode-switch">
            <el-button
              size="small"
              :type="activeMode === 'score' ? 'primary' : ''"
              @click="setMode('score')"
            >
              得分
            </el-button>
            <el-button
              size="small"
              :type="activeMode === 'rank' ? 'primary' : ''"
              @click="setMode('rank')"
            >
              排位
            </el-button>
          </el-button-group>
          <el-button type="text" icon="el-icon-download" @click="handleExport" />
          <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreen" />
        </div>
      </div>
      <div class="chart-container" ref="chart"></div>
    </div>

    <el-dialog
      :visible.sync="dialogVisible"
      :append-to-body="true"
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      width="95%"
      :destroy-on-close="false"
      :show-close="false"
      class="chart-dialog"
      @closed="handleDialogClosed"
      @open="handleDialogOpen"
    >
      <div slot="title" class="dialog-header">
        <span>{{ dialogTitle }}</span>
        <div class="dialog-actions">
          <el-button type="text" icon="el-icon-download" @click="handleExportFullscreen" />
          <el-button type="text" icon="el-icon-close" @click="closeDialog" />
        </div>
      </div>
      <div class="fullscreen-chart" ref="fullscreenChart"></div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '@/utils/request'
import _ from 'lodash'

const YEAR_BASE = 2023
const YEAR_COLORS = [
  '#2E86DE',
  '#E67E22',
  '#9B59B6',
  '#2ECC71',
  '#E74C3C',
  '#1ABC9C',
  '#F1C40F',
  '#34495E',
  '#D35400',
  '#7F8C8D'
]

export default {
  name: 'GameScatterChartMobile',
  data() {
    return {
      chart: null,
      fullscreenChart: null,
      dialogVisible: false,
      dialogTitle: '',
      resizeHandler: null,
      activeMode: 'score',
      chartData: {
        score: [],
        rank: [],
        years: []
      },
      colorMap: {},
      isInitializing: false,
      loading: false
    }
  },
  mounted() {
    this.initChart()
    this.fetchData()
    window.addEventListener('resize', this.resizeCharts)
  },
  beforeDestroy() {
    this.cleanup()
    window.removeEventListener('resize', this.resizeCharts)
  },
  watch: {
    dialogVisible(val) {
      if (!val && this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    }
  },
  methods: {
    initChart() {
      this.chart = echarts.init(this.$refs.chart)
    },
    resizeCharts() {
      this.chart?.resize()
      this.fullscreenChart?.resize()
    },
    cleanup() {
      if (this.chart) {
        this.chart.dispose()
        this.chart = null
      }
      if (this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }
    },
    setMode(mode) {
      if (this.activeMode === mode) return
      this.activeMode = mode
      this.updateChart()
    },
    async fetchData() {
      try {
        this.loading = true
        const res = await request.get('/api/analysis/game/scatter')
        const data = res.data || {}
        this.chartData = {
          score: data.score || [],
          rank: data.rank || [],
          years: data.years || []
        }
        this.applyYearColors()
        this.updateChart()
      } catch (error) {
        console.error('获取赛事散点图数据失败:', error)
        this.$message.error('获取数据失败')
      } finally {
        this.loading = false
      }
    },
    applyYearColors() {
      const sortedYears = (this.chartData.years || []).sort((a, b) => a - b)
      const map = {}
      sortedYears.forEach((year) => {
        const idx = Math.max(0, year - YEAR_BASE)
        map[year] = YEAR_COLORS[idx % YEAR_COLORS.length]
      })
      this.colorMap = map
    },
    getAverages(mode) {
      const list = mode === 'score' ? this.chartData.score : this.chartData.rank
      if (!Array.isArray(list) || list.length === 0) return null
      const yKey = mode === 'score' ? 'score_rate' : 'rank_rate'
      let sumX = 0
      let sumY = 0
      let count = 0
      list.forEach(item => {
        const x = Number(item.avg_difficulty)
        const y = Number(item[yKey])
        if (!isNaN(x) && !isNaN(y)) {
          sumX += x
          sumY += y
          count++
        }
      })
      if (!count) return null
      return {
        avgX: sumX / count,
        avgY: sumY / count
      }
    },
    getSeries(mode, symbolSize = 6) {
      const list = mode === 'score' ? this.chartData.score : this.chartData.rank
      const grouped = _.groupBy(list, 'year')
      const yKey = mode === 'score' ? 'score_rate' : 'rank_rate'
      return Object.keys(grouped).sort((a, b) => a - b).map(year => {
        const items = grouped[year] || []
        return {
          name: `${year}`,
          type: 'scatter',
          symbolSize,
          emphasis: { focus: 'series' },
          itemStyle: { color: this.colorMap[year] || '#2E86DE' },
          data: items.map(item => ({
            name: item.game_name,
            value: [
              Number(item.avg_difficulty || 0),
              Number(item[yKey] || 0)
            ]
          }))
        }
      })
    },
    buildOption(mode, { symbolSize = 6 } = {}) {
      const isRank = mode === 'rank'
      const yLabel = isRank ? '排位率(%)' : '得分率(%)'
      const series = this.getSeries(mode, symbolSize)
      const averages = this.getAverages(mode)
      // X 轴分区背景（低饱和高透明）
      const xBands = [
        { start: 0, end: 14.29, color: 'rgba(255,153,153,0.18)' },   // 红
        { start: 14.29, end: 28.58, color: 'rgba(255,179,102,0.18)' }, // 橙
        { start: 28.58, end: 42.87, color: 'rgba(255,217,102,0.18)' }, // 黄
        { start: 42.87, end: 57.15, color: 'rgba(168,213,168,0.18)' }, // 绿
        { start: 57.15, end: 71.44, color: 'rgba(153,204,255,0.18)' }, // 蓝
        { start: 71.44, end: 85.72, color: 'rgba(204,153,255,0.18)' }, // 紫
        { start: 85.72, end: 100, color: 'rgba(102,102,153,0.18)' }    // 黑
      ]
      const bandSeries = {
        name: 'x-band-background',
        type: 'scatter',
        data: [],
        silent: true,
        tooltip: { show: false },
        z: -1,
        zlevel: -1,
        markArea: {
          silent: true,
          itemStyle: {
            opacity: 1,
            borderWidth: 0
          },
          data: xBands.map(band => ([
            { xAxis: band.start, yAxis: 0, itemStyle: { color: band.color } },
            { xAxis: band.end, yAxis: 100 }
          ]))
        }
      }
      series.unshift(bandSeries)

      if (series.length && averages) {
        series[0].markLine = {
          symbol: 'none',
          lineStyle: {
            type: 'dashed',
            width: 1
          },
          data: [
            {
              xAxis: averages.avgX,
              lineStyle: { color: 'rgba(255,153,153,0.9)' }
            },
            {
              yAxis: averages.avgY,
              lineStyle: { color: 'rgba(102,177,255,0.9)' }
            }
          ]
        }
      }
      return {
        backgroundColor: '#ffffff',
        tooltip: {
          trigger: 'item',
          formatter: (params) => {
            if (!params || !params.data) return ''
            const [x, y] = params.data.value
            return `
              <div style="padding: 4px;">
                <div style="font-weight: bold; margin-bottom: 6px;">${params.data.name || ''}</div>
                <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                  <div>年份：${params.seriesName}</div>
                  <div>平均难度：${(x ?? 0).toFixed(2)}%</div>
                  <div>${yLabel}：${(y ?? 0).toFixed(2)}%</div>
                </div>
              </div>
            `
          },
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 12
          }
        },
        legend: {
          type: 'scroll',
          right: 10,
          top: 10,
          data: (this.chartData.years || []).map(y => `${y}`)
        },
        grid: {
          left: 60,
          right: 20,
          top: 55,
          bottom: 50
        },
        xAxis: {
          type: 'value',
          name: '平均难度(%)',
          min: 0,
          max: 100,
          nameLocation: 'middle',
          nameGap: 30,
          axisLabel: { formatter: '{value}%' },
          splitLine: {
            show: false
          }
        },
        yAxis: {
          type: 'value',
          name: yLabel,
          min: 0,
          max: 100,
          inverse: isRank,
          nameLocation: 'middle',
          nameGap: 40,
          axisLabel: { formatter: '{value}%' },
          splitLine: {
            show: false
          }
        },
        series
      }
    },
    updateChart() {
      if (!this.chart) return
      const option = this.buildOption(this.activeMode)
      this.chart.setOption(option, true)
    },
    handleExport() {
      if (!this.chart) return
      const dataURL = this.chart.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff'
      })
      const link = document.createElement('a')
      link.download = this.activeMode === 'score' ? '赛事散点图-得分.png' : '赛事散点图-排位.png'
      link.href = dataURL
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    handleFullscreen() {
      this.dialogTitle = this.activeMode === 'score' ? '赛事散点图（得分）' : '赛事散点图（排位）'
      this.dialogVisible = true
    },
    handleDialogOpen() {
      if (this.isInitializing) return
      this.isInitializing = true
      this.$nextTick(() => {
        try {
          const dom = this.$refs.fullscreenChart
          if (!dom) return
          this.fullscreenChart = echarts.init(dom)
          const option = this.buildOption(this.activeMode, { symbolSize: 8 })
          if (option) {
            this.fullscreenChart.setOption(option, true)
            this.resizeHandler = _.debounce(() => {
              this.fullscreenChart?.resize()
            }, 100)
            window.addEventListener('resize', this.resizeHandler)
          }
        } catch (err) {
          console.error('初始化全屏散点图失败:', err)
          this.$message.error('初始化全屏图表失败')
        } finally {
          this.isInitializing = false
        }
      })
    },
    handleDialogClosed() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }
      if (this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    },
    closeDialog() {
      this.dialogVisible = false
    },
    handleExportFullscreen() {
      if (!this.fullscreenChart) return
      try {
        const dataURL = this.fullscreenChart.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff'
        })
        const link = document.createElement('a')
        link.download = this.dialogTitle + '.png'
        link.href = dataURL
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('导出全屏图片失败:', error)
        this.$message.error('导出图片失败')
      }
    }
  }
}
</script>

<style scoped>
.game-scatter-mobile {
  width: 100%;
}

.chart-card {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background-color: #2E365A;
  color: #fff;
}

.card-title {
  font-size: 15px;
  font-weight: 500;
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.mode-switch {
  margin-right: 4px;
}

.mode-switch .el-button {
  color: #ffffff;
  background-color: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  height: 32px;
  line-height: 32px;
  padding: 0 12px;
  font-size: 12px;
}

.mode-switch .el-button:hover {
  background-color: rgba(255, 255, 255, 0.3);
  color: #ffffff;
}

.mode-switch .el-button--primary {
  background-color: #ffffff;
  border-color: #ffffff;
  color: #2E365A;
}

.mode-switch .el-button--primary:hover {
  background-color: #ffffff;
  border-color: #ffffff;
  color: #2E365A;
}

.chart-container {
  width: 100%;
  height: 320px;
  padding: 12px;
  box-sizing: border-box;
}

.chart-dialog :deep(.el-dialog__header) {
  padding: 0;
  margin: 0;
}

.chart-dialog :deep(.el-dialog__headerbtn) {
  display: none;
}

.chart-dialog :deep(.el-dialog__body) {
  padding: 0;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background-color: #2E365A;
  color: #fff;
}

.dialog-actions {
  display: flex;
  gap: 6px;
}

.fullscreen-chart {
  width: 100%;
  height: 420px;
  padding: 12px;
  box-sizing: border-box;
}
</style>

